// [Codex 2026-07-03] Shared event contract for app-wide CAO quick annotate.
export const QUICK_ANNOTATE_EVENT = "fintheon:cao-quick-annotate";

export interface QuickAnnotateAnchor {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export interface QuickAnnotateContext {
  selectedText: string;
  mode: "selected-text" | "quick-instruction";
  surface: string;
  label: string;
  resourceId?: string;
  path?: string;
  url: string;
  anchor?: QuickAnnotateAnchor;
  createdAt: string;
}

export function dispatchQuickAnnotateFromSelection(): QuickAnnotateContext {
  const selectedText = readWindowSelection();
  const active = findActiveSurface();
  const detail = buildQuickAnnotateContext({
    selectedText,
    surface: active.surface,
    label: active.label,
    resourceId: active.resourceId,
    path: active.path,
    url: window.location.href,
    anchor: selectedText ? readSelectionAnchor() : undefined,
  });
  window.dispatchEvent(new CustomEvent(QUICK_ANNOTATE_EVENT, { detail }));
  return detail;
}

export function buildQuickAnnotateContext(input: {
  selectedText?: string;
  surface?: string;
  label?: string;
  resourceId?: string;
  path?: string;
  url?: string;
  anchor?: Partial<QuickAnnotateAnchor> | null;
}): QuickAnnotateContext {
  const selectedText = compactText(input.selectedText ?? "");
  return {
    selectedText,
    mode: selectedText ? "selected-text" : "quick-instruction",
    surface: input.surface?.trim() || "app",
    label: input.label?.trim() || "Fintheon",
    resourceId: input.resourceId?.trim() || undefined,
    path: input.path?.trim() || undefined,
    url: input.url?.trim() || "",
    anchor: normalizeAnchor(input.anchor),
    createdAt: new Date().toISOString(),
  };
}

export function readQuickAnnotateEvent(
  event: Event,
): QuickAnnotateContext | null {
  const detail = (event as CustomEvent).detail as Partial<QuickAnnotateContext>;
  if (!detail || typeof detail !== "object") return null;
  return buildQuickAnnotateContext(detail);
}

function readWindowSelection(): string {
  const selection = window.getSelection?.();
  return compactText(selection?.toString() ?? "");
}

function readSelectionAnchor(): QuickAnnotateAnchor | undefined {
  const range = window.getSelection?.()?.rangeCount
    ? window.getSelection()?.getRangeAt(0)
    : null;
  if (!range) return undefined;
  const clientRects = Array.from(range.getClientRects());
  const firstVisibleRect = clientRects.find(
    (rect) => rect.width > 0 && rect.height > 0,
  );
  return (
    normalizeAnchor(range.getBoundingClientRect()) ??
    normalizeAnchor(firstVisibleRect)
  );
}

function normalizeAnchor(
  anchor?: Partial<QuickAnnotateAnchor> | DOMRect | null,
): QuickAnnotateAnchor | undefined {
  if (!anchor) return undefined;
  const left = Number(anchor.left);
  const top = Number(anchor.top);
  const width = Number(anchor.width);
  const height = Number(anchor.height);
  const right =
    typeof anchor.right === "number" ? Number(anchor.right) : left + width;
  const bottom =
    typeof anchor.bottom === "number" ? Number(anchor.bottom) : top + height;
  const values = [top, left, right, bottom, width, height];
  if (values.some((value) => !Number.isFinite(value))) return undefined;
  if (width <= 0 || height <= 0) return undefined;
  return { top, left, right, bottom, width, height };
}

function findActiveSurface(): {
  surface: string;
  label: string;
  resourceId?: string;
  path?: string;
} {
  const selectionSurface = findSelectionSurface();
  if (selectionSurface) return selectionSurface;
  const active = document.activeElement;
  const surface = active?.closest?.("[data-quick-annotate-surface]");
  if (surface instanceof HTMLElement) {
    return {
      surface: surface.dataset.quickAnnotateSurface || "app",
      label:
        surface.dataset.quickAnnotateLabel ||
        surface.getAttribute("aria-label") ||
        "Fintheon",
      resourceId: surface.dataset.quickAnnotateResourceId,
      path: surface.dataset.quickAnnotatePath,
    };
  }
  return { surface: "app", label: document.title || "Fintheon" };
}

function findSelectionSurface(): {
  surface: string;
  label: string;
  resourceId?: string;
  path?: string;
} | null {
  const node = window.getSelection?.()?.anchorNode;
  const element =
    node instanceof HTMLElement ? node : (node?.parentElement ?? null);
  const surface = element?.closest?.("[data-quick-annotate-surface]");
  if (!(surface instanceof HTMLElement)) return null;
  return {
    surface: surface.dataset.quickAnnotateSurface || "app",
    label:
      surface.dataset.quickAnnotateLabel ||
      surface.getAttribute("aria-label") ||
      "Fintheon",
    resourceId: surface.dataset.quickAnnotateResourceId,
    path: surface.dataset.quickAnnotatePath,
  };
}

function compactText(value: string): string {
  return value.replace(/\s+/g, " ").trim().slice(0, 4000);
}
